import { AssetManagementItem } from 'src/app/api/models';

/** Seriali richiesti per chiamata al BE: l'elenco viaggia nella query string. */
export const SERIAL_PASTE_CHUNK_SIZE = 100;

export interface ParsedSerialList {
  /** Seriali univoci (senza distinzione tra maiuscole e minuscole), nell'ordine in cui sono stati incollati. */
  serials: string[];
  /** Seriali ripetuti nell'elenco incollato (una voce per ogni ripetizione). */
  duplicates: string[];
}

/** Elenco incollato: separatori virgola, punto e virgola, tabulazione e a capo (es. colonna di Excel o CSV). */
export function parseSerialList(text: string | null | undefined): ParsedSerialList {
  const seen = new Set<string>();
  const serials: string[] = [];
  const duplicates: string[] = [];
  for (const raw of (text ?? '').split(/[,;\t\r\n]+/)) {
    const serial = raw.trim();
    if (!serial) continue;
    const key = serial.toUpperCase();
    if (seen.has(key)) {
      duplicates.push(serial);
      continue;
    }
    seen.add(key);
    serials.push(serial);
  }
  return { serials, duplicates };
}

export function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    out.push(items.slice(i, i + size));
  }
  return out;
}

export interface SerialPasteScope {
  warehouse_id?: number;
  product_id?: number;
  purpose?: number;
  agreement_id?: number;
  /** Solo i seriali assegnati a questa consegna (picker del DDT). */
  delivery_id?: number;
}

export interface SerialPasteIssue {
  serial: string;
  reason: string;
}

export interface SerialPasteReport {
  /** Seriali distinti richiesti. */
  requested: number;
  /** Trovati con i filtri del picker e selezionabili (anche quelli già selezionati prima). */
  selectable: AssetManagementItem[];
  /** Trovati con i filtri del picker ma non selezionabili (già in un'altra consegna o già partiti con un DDT). */
  blocked: SerialPasteIssue[];
  /** Non trovati con i filtri del picker, con il motivo se esistono altrove. */
  notFound: SerialPasteIssue[];
  /** Ripetuti nell'elenco incollato e ignorati. */
  duplicates: string[];
}

const purposeLabel = (purpose: number) => (purpose === 1 ? 'Vendita' : 'Manutenzione');

/** Perché un seriale trovato fuori dai filtri del picker non compare tra i risultati. */
export function whyNotInScope(asset: AssetManagementItem, scope: SerialPasteScope): string {
  if (scope.warehouse_id != null && asset.related_warehouse?.id !== scope.warehouse_id) {
    if (asset.related_warehouse) {
      return `In un altro magazzino: ${asset.related_warehouse.name}`;
    }
    return asset.registry_end_customer
      ? `Presso il cliente ${asset.registry_end_customer.name}`
      : 'Non è a magazzino';
  }
  if (
    scope.product_id != null &&
    asset.related_product &&
    asset.related_product.id !== scope.product_id
  ) {
    return `Appartiene a un altro prodotto: ${asset.related_product.name}`;
  }
  if (scope.delivery_id != null && asset.related_delivery_id !== scope.delivery_id) {
    return asset.related_delivery_id
      ? `Assegnato a un'altra consegna (#${asset.related_delivery_id})`
      : 'Non assegnato a questa consegna';
  }
  if (scope.purpose != null && asset.purpose !== scope.purpose) {
    return `Utilizzo diverso: ${purposeLabel(asset.purpose)}`;
  }
  if (
    scope.agreement_id != null &&
    asset.related_agreement &&
    asset.related_agreement.id !== scope.agreement_id
  ) {
    return `Appartiene a un'altra commessa: ${asset.related_agreement.name}`;
  }
  return 'Non disponibile con i filtri attuali';
}

/**
 * Confronta i seriali incollati con quelli trovati.
 * - `inScope`: risultati con i filtri del picker (magazzino, prodotto, utilizzo, commessa);
 * - `elsewhere`: risultati della stessa ricerca senza quei filtri, per spiegare i mancanti.
 */
export function classifyPastedSerials(input: {
  parsed: ParsedSerialList;
  inScope: AssetManagementItem[];
  elsewhere: AssetManagementItem[];
  scope: SerialPasteScope;
  blockReason: (asset: AssetManagementItem) => string | null;
}): SerialPasteReport {
  const { parsed, inScope, elsewhere, scope, blockReason } = input;
  const byKey = (list: AssetManagementItem[]) => {
    const map = new Map<string, AssetManagementItem>();
    for (const a of list) map.set(a.serial_number.toUpperCase(), a);
    return map;
  };
  const scoped = byKey(inScope);
  const other = byKey(elsewhere);

  const report: SerialPasteReport = {
    requested: parsed.serials.length,
    selectable: [],
    blocked: [],
    notFound: [],
    duplicates: parsed.duplicates,
  };
  for (const serial of parsed.serials) {
    const key = serial.toUpperCase();
    const found = scoped.get(key);
    if (found) {
      const reason = blockReason(found);
      if (reason) {
        report.blocked.push({ serial, reason });
      } else {
        report.selectable.push(found);
      }
      continue;
    }
    const elsewhereAsset = other.get(key);
    report.notFound.push({
      serial,
      reason: elsewhereAsset ? whyNotInScope(elsewhereAsset, scope) : 'Seriale inesistente',
    });
  }
  return report;
}
